import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Copy, CheckCircle, Key } from '@phosphor-icons/react';
import { adminAPI } from '../../lib/api';

const PLACEHOLDER_DOMAIN = '@placeholder.local';

/**
 * Admin-only dialog that issues a one-time claim link for a manual profile.
 *
 * If the profile still has a placeholder `@placeholder.local` email, the
 * admin is REQUIRED to enter a real email first. The backend will update
 * both the auth user's email and the profile's email before minting the
 * token.
 *
 * After generation, the dialog displays the full claim URL (built from
 * window.location.origin so it works in both Preview and production) with
 * a one-click copy button. The link is shown ONCE — re-opening this
 * dialog will void the previous token and mint a new one.
 */
export default function ClaimLinkDialog({ open, onClose, profile, onIssued }) {
  const [email, setEmail] = useState('');
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const isPlaceholder = !!profile?.email && profile.email.toLowerCase().endsWith(PLACEHOLDER_DOMAIN);

  useEffect(() => {
    if (open) {
      setEmail(isPlaceholder ? '' : (profile?.email || ''));
      setLink(null);
    }
  }, [open, profile?.email, isPlaceholder]);

  if (!open || !profile) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (isPlaceholder && !email.trim()) {
      toast.error('Please enter a real email for this player.');
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      // Only send `email` when it represents a change (placeholder → real
      // OR admin explicitly typed something different).
      const trimmed = email.trim().toLowerCase();
      if (trimmed && trimmed !== (profile?.email || '').toLowerCase()) {
        payload.email = trimmed;
      }
      const res = await adminAPI.generateClaimLink(profile.profile_id, payload);
      const url = `${window.location.origin}${res.data.claim_path}`;
      setLink({
        url,
        email: res.data.email,
        expiresAt: res.data.expires_at,
        ttlDays: res.data.ttl_days,
      });
      onIssued?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate claim link.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success('Claim link copied');
    } catch {
      toast.error('Could not copy — please select and copy manually.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="claim-link-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5 space-y-4"
        data-testid="claim-link-dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl text-[#013523]">
              {link ? 'Claim link ready' : 'Generate claim link'}
            </h3>
            <p className="text-xs text-[#6a766e] mt-1">
              {link
                ? 'Share this link with the player out-of-band (text, email, etc). It expires after ' + link.ttlDays + ' days and works once.'
                : `Lets ${profile.full_name || 'this player'} set their own password and log in.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="claim-link-close"
            className="text-[#6a766e] hover:text-[#013523]"
          >
            <X size={20} />
          </button>
        </div>

        {!link && (
          <form onSubmit={submit} className="space-y-3">
            {isPlaceholder ? (
              <div className="text-xs text-[#a8580f] bg-[#fff5e6] border border-[#f4d29c] rounded-lg p-3">
                This player was created without a real email. Enter their email below — they&apos;ll use it to log in.
              </div>
            ) : (
              <div className="text-xs text-[#6a766e]">
                Player will log in with: <span className="font-medium text-[#013523]">{profile.email}</span>
              </div>
            )}

            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">
                {isPlaceholder ? 'Email *' : 'Email (update if needed)'}
              </span>
              <input
                data-testid="claim-link-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={isPlaceholder}
                autoFocus={isPlaceholder}
                placeholder="player@example.com"
                className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
              />
            </label>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                data-testid="claim-link-cancel"
                className="px-3 py-2 rounded-lg text-sm border border-[#e3dcd1] text-[#6a766e] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="claim-link-submit"
                className="px-4 py-2 rounded-lg text-sm bg-[#015e3f] text-white font-medium hover:bg-[#013523] disabled:opacity-50 flex items-center gap-2"
              >
                <Key size={14} weight="bold" />
                {busy ? 'Generating…' : 'Generate claim link'}
              </button>
            </div>
          </form>
        )}

        {link && (
          <div className="space-y-3" data-testid="claim-link-result">
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Login email</div>
              <div className="text-sm text-[#013523]" data-testid="claim-link-result-email">{link.email}</div>
            </div>
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Claim URL</div>
              <div className="flex items-stretch gap-2">
                <input
                  readOnly
                  data-testid="claim-link-result-url"
                  value={link.url}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-xs font-mono"
                />
                <button
                  type="button"
                  onClick={copy}
                  data-testid="claim-link-copy"
                  className="px-3 py-2 rounded-lg text-xs bg-[#015e3f] text-white font-medium flex items-center gap-1.5"
                >
                  <Copy size={14} weight="bold" />
                  Copy
                </button>
              </div>
            </div>
            <div className="text-[11px] text-[#6a766e] flex items-center gap-1.5">
              <CheckCircle size={12} weight="bold" />
              One-time use. Expires {new Date(link.expiresAt).toLocaleDateString()}.
            </div>
            <div className="pt-1 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                data-testid="claim-link-done"
                className="px-4 py-2 rounded-lg text-sm bg-[#013523] text-white"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
